import axios from "axios";

/**
 * Global Network Manager for eTour Web App
 * Detects online/offline events, performs active ping checks,
 * listens to Axios network dropouts, and coordinates UI notifications.
 */

class NetworkManager {
  constructor() {
    this.isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
    this.isChecking = false;
    this.wasOffline = false;
    this.subscribers = new Set();
    this.heartbeatTimer = null;
    this.simulatedOffline = false;

    if (typeof window !== "undefined") {
      this.initEventListeners();
      this.initAxiosInterceptor();
    }
  }

  initEventListeners() {
    window.addEventListener("online", () => {
      this.handleBrowserEvent(true);
    });

    window.addEventListener("offline", () => {
      this.handleBrowserEvent(false);
    });

    // Expose simulation helper on window for developer testing
    window.__simulateOffline = (simulate = true) => {
      this.simulatedOffline = simulate;
      if (simulate) {
        this.setOnlineStatus(false);
      } else {
        this.checkConnectivity();
      }
      return `Simulate offline set to: ${simulate}`;
    };
  }

  initAxiosInterceptor() {
    try {
      axios.interceptors.response.use(
        (response) => {
          // If we had been marked offline, a successful API response proves we are back online
          if (!this.isOnline && !this.simulatedOffline) {
            this.setOnlineStatus(true);
          }
          return response;
        },
        (error) => {
          // Detect pure network errors (e.g. no internet, DNS failure, connection refused)
          const isNetErr =
            error.code === "ERR_NETWORK" ||
            error.message === "Network Error" ||
            (!error.response && typeof navigator !== "undefined" && !navigator.onLine);

          if (isNetErr && !this.simulatedOffline) {
            this.checkConnectivity();
          }

          return Promise.reject(error);
        }
      );
    } catch (e) {
      // Axios interceptor initialization fallback
      console.warn("Axios interceptor could not be attached:", e);
    }
  }

  handleBrowserEvent(browserOnline) {
    if (this.simulatedOffline) return;

    if (!browserOnline) {
      this.setOnlineStatus(false);
      this.startHeartbeat();
    } else {
      // Browser reports online, verify actively
      this.checkConnectivity();
    }
  }

  setOnlineStatus(online) {
    const previousState = this.isOnline;

    if (previousState !== online) {
      if (!online) {
        this.wasOffline = true;
        this.isOnline = false;
        this.startHeartbeat();
      } else {
        this.isOnline = true;
        this.stopHeartbeat();
      }
      this.notify();
    }
  }

  /**
   * Actively tests connectivity via a cache-busted fetch request.
   * Resolves whether internet connectivity is genuinely functional.
   */
  async checkConnectivity() {
    if (this.simulatedOffline) {
      this.setOnlineStatus(false);
      return false;
    }

    this.isChecking = true;
    this.notify();

    // Check fast browser navigator flag first
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      this.isChecking = false;
      this.setOnlineStatus(false);
      return false;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);

    try {
      // Ping local server or public endpoint with timestamp to bypass browser cache
      const pingUrl = `${window.location.origin}/favicon.ico?_probe=${Date.now()}`;
      await fetch(pingUrl, {
        method: "HEAD",
        mode: "no-cors",
        cache: "no-store",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      this.isChecking = false;
      this.setOnlineStatus(true);
      return true;
    } catch {
      clearTimeout(timeoutId);
      // Secondary fallback probe if origin fetch failed
      try {
        const fallbackController = new AbortController();
        const fbTimeout = setTimeout(() => fallbackController.abort(), 2500);
        await fetch(`https://www.google.com/favicon.ico?_probe=${Date.now()}`, {
          method: "HEAD",
          mode: "no-cors",
          cache: "no-store",
          signal: fallbackController.signal,
        });
        clearTimeout(fbTimeout);
        this.isChecking = false;
        this.setOnlineStatus(true);
        return true;
      } catch {
        this.isChecking = false;
        this.setOnlineStatus(false);
        return false;
      }
    }
  }

  startHeartbeat() {
    if (this.heartbeatTimer) return;
    // Check every 6 seconds while offline to auto-recover when internet is back
    this.heartbeatTimer = setInterval(() => {
      if (!this.isOnline && !this.simulatedOffline) {
        this.checkConnectivity();
      }
    }, 6000);
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  clearWasOffline() {
    this.wasOffline = false;
    this.notify();
  }

  getState() {
    return {
      isOnline: this.isOnline,
      wasOffline: this.wasOffline,
      isChecking: this.isChecking,
    };
  }

  subscribe(callback) {
    this.subscribers.add(callback);
    callback(this.getState());

    return () => {
      this.subscribers.delete(callback);
    };
  }

  notify() {
    const state = this.getState();
    this.subscribers.forEach((cb) => {
      try {
        cb(state);
      } catch (err) {
        console.error("NetworkManager subscriber error:", err);
      }
    });
  }
}

export const networkManager = new NetworkManager();
export default networkManager;
